import { FC, useEffect, useMemo, useState } from "react";
import classes from './calendarMonth.module.scss'
import { calendarService, ICalendarDay, ICalendarFilter } from "../../../../entities/calendar";
import { MyError } from "../../../../shared/lib/error/MyError";
import { AuthError } from "../../../../shared/lib/error/AuthError";
import { useMyActions } from "../../../../entities/my";
import { useGlobalMessageActions } from "../../../../entities/globalMessage";
import { useGetMonthWeeks } from "../../../../shared/lib/hooks/useGetMonthWeeks";
import { LoaderSpinner } from "../../../../shared/ui/spinner";

interface IProps {
    filter: ICalendarFilter;
    onSelectDate: (date: Date) => void;
    omitUserIds?: boolean;
}

export const CalendarMonth: FC<IProps> = ({filter, onSelectDate, omitUserIds = false}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    const [visibleMonth, setVisibleMonth] = useState<Date>(new Date())
    const [calendarDays, setCalendarDays] = useState<ICalendarDay[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const weeks = useGetMonthWeeks(visibleMonth)
    const monthTitle = visibleMonth
        .toLocaleString('ru-RU', {month: 'long', year: 'numeric'})
        .replace(/\s*г\.$/, '')

    const calendarDaysMap = useMemo(() => {
        return new Map(
            calendarDays.map(day => [
                `${day.date.year}-${day.date.month}-${day.date.day}`,
                day,
            ])
        )
    }, [calendarDays])

    const getCalendarDayInfo = (date: Date) => {
        return calendarDaysMap.get(
            `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
        )
    }

    const getListInfo = (date: Date) => {
        const calendarDay = getCalendarDayInfo(date)

        if(!calendarDay) return null

        const items = [
            {label: 'Запланированные', count: calendarDay.scheduledCount, className: classes.scheduled},
            {label: 'Проведённые', count: calendarDay.completedCount, className: classes.completed},
            {label: 'Отменённые', count: calendarDay.cancelledCount, className: classes.cancelledInfo},
            {label: 'Перенесённые', count: calendarDay.rescheduledCount, className: classes.rescheduledInfo},
        ].filter(item => item.count > 0)

        if(!items.length) return null

        return (
            <ul className={classes.infoList}>
                {items.map(item => (
                    <li className={`${classes.item} ${item.className}`} key={item.label}>
                        <span className={classes.infoLabel}>{item.label}</span>
                        <span className={classes.infoCount}>{item.count}</span>
                    </li>
                ))}
            </ul>
        )
    }

    const getDayMarkers = (date: Date) => {
        const calendarDay = getCalendarDayInfo(date)

        if(!calendarDay) return null

        return (
            <>
                {calendarDay.scheduledCount > 0 && (
                    <span className={`${classes.marker} ${classes.scheduledMarker}`} aria-hidden="true" />
                )}
                {calendarDay.cancelledCount > 0 && (
                    <span className={`${classes.marker} ${classes.cancelledMarker}`} aria-hidden="true" />
                )}
                {calendarDay.rescheduledCount > 0 && (
                    <span className={`${classes.marker} ${classes.rescheduledMarker}`} aria-hidden="true" />
                )}
                {calendarDay.completedCount > 0 && (
                    <span className={`${classes.marker} ${classes.completedMarker}`} aria-hidden="true" />
                )}
            </>
        )
    }

    useEffect(() => {
        let isActual = true
        setCalendarDays([])

        const getData = async() => {
            try{
                setIsLoading(true)
                const {userIds: _userIds, ...filterWithoutUserIds} = filter
                const response = await calendarService.getMonthCalendar({
                    year: visibleMonth.getFullYear(),
                    month: visibleMonth.getMonth() + 1,
                    filter: omitUserIds ? filterWithoutUserIds : filter,
                })

                if(isActual) setCalendarDays(response.days)
            }
            catch(e){
                console.log(e)
                if(e instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else if(e instanceof MyError){
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при получении календаря на день', type: 'error'})
                }
            }
            finally{
                setIsLoading(false)
            }
        }

        getData()

        return () => {
            isActual = false
        }
    }, [visibleMonth, filter, omitUserIds])

    return (
        isLoading
            ?
        <section className={classes.loader}>
            <LoaderSpinner />
        </section>
            :
        <section className={classes.container}>
            <section className={classes.header}>
                <h2 className={classes.title}>{monthTitle}</h2>
                <nav className={classes.navigation} aria-label="Переключение месяца">
                    <button
                        type="button"
                        className={classes.navigationButton}
                        onClick={() => {
                            setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1))
                        }}
                        aria-label="Предыдущий месяц"
                    >
                        ‹
                    </button>
                    <button
                        type="button"
                        className={classes.navigationButton}
                        onClick={() => {
                            setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1))
                        }}
                        aria-label="Следующий месяц"
                    >
                        ›
                    </button>
                </nav>
            </section>

            <section className={classes.calendarFrame}>
                <table className={classes.month}>
                    <thead>
                        <tr>
                            <th scope="col">Пн</th>
                            <th scope="col">Вт</th>
                            <th scope="col">Ср</th>
                            <th scope="col">Чт</th>
                            <th scope="col">Пт</th>
                            <th scope="col">Сб</th>
                            <th scope="col">Вс</th>
                        </tr>
                    </thead>
                    <tbody>
                        {weeks.map((week, weekIndex) => (
                            <tr key={weekIndex}>
                                {week.map((day) => {
                                    const isUnavailable = getCalendarDayInfo(day.date)?.isAvailable === false

                                    return (
                                        <td
                                            key={day.date.getTime()}
                                            tabIndex={day.class === 'opacity' ? -1 : 0}
                                            onClick={day.class === 'opacity' ? undefined : () => onSelectDate(day.date)}
                                            onKeyDown={(event) => {
                                                if(day.class === 'opacity') return
                                                if(event.key === 'Enter' || event.key === ' '){
                                                    event.preventDefault()
                                                    onSelectDate(day.date)
                                                }
                                            }}
                                            className={classes.day +
                                                (day.class === 'current' ? ` ${classes.current}` : '') +
                                                (day.class === 'opacity' ? ` ${classes.opacity}` : '') +
                                                (isUnavailable ? ` ${classes.unavailable}` : '')
                                            }
                                        >
                                            {day.class !== 'opacity' && (
                                                <>
                                                    <span className={classes.dayNumber}>{day.date.getDate()}</span>
                                                    {getDayMarkers(day.date)}
                                                    {getListInfo(day.date)}
                                                </>
                                            )}
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </section>
        </section>
    )
}
